/**
 * Server-side SEO configuration.
 *
 * Kept separate from `@src/configs/environments` because that module derives
 * values from `window` and is therefore client-oriented. Everything here runs
 * during metadata generation, sitemap builds and server rendering.
 */

const NETWORK_ORIGINS: Record<string, string> = {
  MAINNET: 'https://drep.id',
  PREPROD: 'https://preprod.drep.id',
  PREVIEW: 'https://preview.drep.id',
};

function normalizeOrigin(value: string): string {
  const withProtocol = /^https?:\/\//.test(value) ? value : `https://${value}`;
  return withProtocol.replace(/\/+$/, '');
}

/**
 * Resolved at runtime, not build time.
 *
 * `NEXT_PUBLIC_*` variables are inlined as string literals by webpack during
 * the build — in the server bundle as well as the client one. CI builds a
 * single image from the mainnet compose file and deploys it to mainnet,
 * preprod, preview and dev, so anything read from a `NEXT_PUBLIC_` variable is
 * frozen to whatever the build machine had. That is how production came to
 * serve `Disallow: /`: the build had no network set, the PREVIEW default was
 * compiled in, and no runtime value could override it.
 *
 * Non-prefixed variables are read from the real environment on the server, so
 * `CARDANO_NETWORK` and `SITE_URL` are the authoritative pair. The
 * `NEXT_PUBLIC_` fallbacks stay for local development.
 */
export const network = (
  process.env.CARDANO_NETWORK ||
  process.env.NETWORK_NAME ||
  process.env.NEXT_PUBLIC_CARDANO_NETWORK ||
  'PREVIEW'
).toUpperCase();

/** Only mainnet may be indexed. Testnets serve a byte-identical duplicate. */
export const isIndexableDeployment = network === 'MAINNET';

/**
 * Absolute origin of this deployment, e.g. `https://drep.id`.
 * Absolute URLs are required in sitemaps, canonicals and JSON-LD.
 */
export const siteUrl = normalizeOrigin(
  process.env.SITE_URL ||
    process.env.BASE_DOMAIN ||
    process.env.NEXT_PUBLIC_SITE_URL ||
    NETWORK_ORIGINS[network] ||
    NETWORK_ORIGINS.PREVIEW
);

/** Base URL for server-to-server API calls (container-internal where available). */
export const serverApiUrl = (
  process.env.INTERNAL_API_URL ||
  process.env.NEXT_PUBLIC_BASE_API_URL ||
  `${siteUrl}/api`
).replace(/\/+$/, '');

export function absoluteUrl(path: string): string {
  if (/^https?:\/\//.test(path)) return path;
  return `${siteUrl}${path.startsWith('/') ? path : `/${path}`}`;
}
